/**
 * Static EN ⇄ HI dictionary for community onboarding screens.
 *
 * Keep keys grouped by page/feature so they're easy to scan. When a Hindi
 * value is missing the context falls back to the English value — no
 * placeholder strings or untranslated junk reach the user.
 *
 * Hindi values use Unicode escapes intentionally so this file is safe to
 * edit / diff in any environment regardless of font support.
 */
const communityI18n = {
  en: {
    common: {
      continue: 'Continue',
      back: 'Back',
      cancel: 'Cancel',
      next: 'Next',
      submit: 'Submit',
      please_wait: 'Please wait...',
      something_went_wrong: 'Something went wrong',
    },
    lang: {
      toggle_to_hi: '\u0939\u093F\u0902\u0926\u0940',          // हिंदी
      toggle_to_en: 'English',
      switch_aria: 'Switch language',
    },
    login: {
      app_name: 'SocialStar Community',
      tagline: 'Join the community, learn, grow, contribute',
      sign_in: 'Sign In',
      register: 'Register',
      username_placeholder: 'Username',
      email_placeholder: 'Email',
      password_placeholder: 'Password',
      create_account: 'Create Account',
      sign_in_btn: 'Sign In',
      forgot_password: 'Forgot password?',
    },
    org: {
      // Organiser registration page
      page_title: 'Become an Organiser',
      page_subtitle: 'Tell us a bit about your work so members can find you.',
      step_org: 'Organisation',
      step_contact: 'Contact',
      step_focus: 'Focus',
      step_account: 'Account',
      org_name_label: 'Organisation Name *',
      org_name_placeholder: 'Your collective, NGO or initiative',
      contact_person_label: 'Contact Person Name *',
      contact_person_placeholder: 'Primary contact',
      contact_number_label: 'Contact Number *',
      contact_number_placeholder: '+91 9876543210',
      alt_contact_person_label: 'Alt. Contact Person Name',
      alt_contact_person_placeholder: 'Backup (optional)',
      alt_contact_number_label: 'Alt. Contact Number',
      alt_contact_number_placeholder: 'Backup (optional)',
      objective_label: 'Your Objective *',
      objective_placeholder: 'What change do you want to see?',
      age_group_label: 'Age Group Focus',
      age_group_placeholder: 'e.g. Youth (15\u201324), Adults',
      location_label: 'Primary Location',
      location_placeholder: 'City or area',
      outreach_label: 'Outreach Reach',
      outreach_placeholder: 'How many people can you reach?',
      email_label: 'Email *',
      email_placeholder: 'you@example.com',
      password_label: 'Password *',
      password_placeholder: 'Choose a secure password',
      submit: 'Submit Application',
      success_title: 'Application Submitted',
      success_body: "Thank you. We'll review your details and get back to you over email shortly.",
      go_home: 'Continue to community',
    },
    tour: {
      // Post-signup welcome tour (CommunityOnboarding.js)
      back: 'Back',
      next: 'Next',
      lets_go: "Let's go",
      stars_earned_50: '+50 Stars Earned!',
      welcome_subtitle: 'Welcome to the community',
      // Card 1
      welcome_title: 'Welcome to socialstar',
      welcome_body: 'where your choices shape your journey and earn while you learn',
      feature_exposure: 'Gain exposure and grow your network',
      feature_learning: 'Learn new skills and build confidence',
      feature_enjoy: 'Participate in activities you enjoy',
      feature_rewards: 'Earn rewards for your participation',
      age_confirm: 'I confirm that I am 18 years of age or older and agree to the socialstar community terms.',
      // Card 2
      events_near_you_title: 'Events near you',
      events_near_you_body: 'Events happening in your city \u2014 interest clubs, learning sessions and collaborations',
      sample_chip_sports: 'SPORTS',
      sample_chip_stars: '30 stars',
      sample_action_title: 'Join a gully cricket match',
      sample_action_sub: 'Play together and earn rewards',
      label_pincode: 'YOUR PINCODE',
      placeholder_pincode: 'Enter 6-digit pincode',
      pincode_privacy: 'Used only to show you actions in your area. Never shared with third parties.',
      // Card 3
      join_events_title: 'Join events you care about',
      join_events_body: "Tell us what matters to you \u2014 we'll surface the right actions and events in your feed.",
      label_interests: 'INTERESTS',
      label_income: 'ANNUAL INCOME',
      interest_fun: 'Fun & Sport',
      interest_learning: 'Learning and growth',
      interest_creativity: 'Creativity',
      interest_entertainment: 'Entertainment',
      income_low_line1: 'Below \u20B92 lakh',
      income_low_line2: 'per year',
      income_high_line1: '\u20B92 lakh & above',
      income_high_line2: 'per year',
      income_privacy: 'Income info helps us connect people who need support with the right opportunities. Kept private.',
      // Card 4
      earn_stars_title: 'Earn stars, get real rewards',
      earn_stars_body: 'Every action you complete earns stars. Redeem them for vouchers and recharges directly in the app.',
      reward_vouchers: 'Vouchers',
      reward_vouchers_sub: 'Groceries, shopping & more',
      reward_mobile: 'Mobile recharge',
      reward_mobile_sub: 'Any network',
      reward_more: '& many more',
      reward_more_sub: 'New rewards added regularly',
      badge_redeem: 'Redeem',
      badge_soon: 'Soon',
      all_set: "You're all set \u2014 Start completing actions and watch your stars grow.",
    },
  },

  hi: {
    common: {
      continue: '\u0906\u0917\u0947 \u092C\u0922\u093C\u0947\u0902',
      back: '\u0935\u093E\u092A\u0938',
      cancel: '\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902',
      next: '\u0905\u0917\u0932\u093E',
      submit: '\u091C\u092E\u093E \u0915\u0930\u0947\u0902',
      please_wait: '\u0915\u0943\u092A\u092F\u093E \u092A\u094D\u0930\u0924\u0940\u0915\u094D\u0937\u093E \u0915\u0930\u0947\u0902...',
      something_went_wrong: '\u0915\u0941\u091B \u0917\u0932\u0924 \u0939\u094B \u0917\u092F\u093E',
    },
    lang: {
      toggle_to_hi: '\u0939\u093F\u0902\u0926\u0940',
      toggle_to_en: 'English',
      switch_aria: '\u092D\u093E\u0937\u093E \u092C\u0926\u0932\u0947\u0902',
    },
    login: {
      app_name: '\u0938\u094B\u0936\u0932\u0938\u094D\u091F\u093E\u0930 \u0915\u092E\u094D\u092F\u0941\u0928\u093F\u091F\u0940',
      tagline: '\u0938\u092E\u0941\u0926\u093E\u092F \u0938\u0947 \u091C\u0941\u0921\u093C\u0947\u0902, \u0938\u0940\u0916\u0947\u0902, \u092C\u0922\u093C\u0947\u0902 \u0914\u0930 \u092F\u094B\u0917\u0926\u093E\u0928 \u0926\u0947\u0902',
      sign_in: '\u0938\u093E\u0907\u0928 \u0907\u0928',
      register: '\u092A\u0902\u091C\u0940\u0915\u0930\u0923',
      username_placeholder: '\u092F\u0942\u091C\u093C\u0930\u0928\u0947\u092E',
      email_placeholder: '\u0908\u092E\u0947\u0932',
      password_placeholder: '\u092A\u093E\u0938\u0935\u0930\u094D\u0921',
      create_account: '\u0916\u093E\u0924\u093E \u092C\u0928\u093E\u090F\u0902',
      sign_in_btn: '\u0938\u093E\u0907\u0928 \u0907\u0928',
      forgot_password: '\u092A\u093E\u0938\u0935\u0930\u094D\u0921 \u092D\u0942\u0932 \u0917\u090F?',
    },
    org: {
      page_title: '\u0906\u092F\u094B\u091C\u0915 \u092C\u0928\u0947\u0902',
      page_subtitle: '\u0905\u092A\u0928\u0947 \u0915\u093E\u092E \u0915\u0947 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902 \u0939\u092E\u0947\u0902 \u092C\u0924\u093E\u090F\u0902 \u0924\u093E\u0915\u093F \u0938\u0926\u0938\u094D\u092F \u0906\u092A\u0915\u094B \u0922\u0942\u0902\u0922 \u0938\u0915\u0947\u0902\u0964',
      step_org: '\u0938\u0902\u0917\u0920\u0928',
      step_contact: '\u0938\u0902\u092A\u0930\u094D\u0915',
      step_focus: '\u0915\u093E\u092E\u0915\u093E\u091C',
      step_account: '\u0916\u093E\u0924\u093E',
      org_name_label: '\u0938\u0902\u0917\u0920\u0928 \u0915\u093E \u0928\u093E\u092E *',
      org_name_placeholder: '\u0906\u092A\u0915\u093E \u0917\u094D\u0930\u0941\u092A, \u090F\u0928\u091C\u0940\u0913 \u092F\u093E \u092A\u0939\u0932',
      contact_person_label: '\u0938\u0902\u092A\u0930\u094D\u0915 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0915\u093E \u0928\u093E\u092E *',
      contact_person_placeholder: '\u092A\u094D\u0930\u093E\u0925\u092E\u093F\u0915 \u0938\u0902\u092A\u0930\u094D\u0915',
      contact_number_label: '\u0938\u0902\u092A\u0930\u094D\u0915 \u0928\u0902\u092C\u0930 *',
      contact_number_placeholder: '+91 9876543210',
      alt_contact_person_label: '\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915 \u0938\u0902\u092A\u0930\u094D\u0915 \u0935\u094D\u092F\u0915\u094D\u0924\u093F',
      alt_contact_person_placeholder: '\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915 (\u090A\u092A\u091A\u093E\u0930\u093F\u0915)',
      alt_contact_number_label: '\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915 \u0938\u0902\u092A\u0930\u094D\u0915 \u0928\u0902\u092C\u0930',
      alt_contact_number_placeholder: '\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915 (\u090A\u092A\u091A\u093E\u0930\u093F\u0915)',
      objective_label: '\u0906\u092A\u0915\u093E \u0909\u0926\u094D\u0926\u0947\u0936\u094D\u092F *',
      objective_placeholder: '\u0906\u092A \u0915\u094D\u092F\u093E \u092C\u0926\u0932\u093E\u0935 \u091A\u093E\u0939\u0924\u0947 \u0939\u0948\u0902?',
      age_group_label: '\u0906\u092F\u0941 \u0935\u0930\u094D\u0917 \u092A\u0930 \u0915\u0947\u0902\u0926\u094D\u0930\u093F\u0924',
      age_group_placeholder: '\u091C\u0948\u0938\u0947 \u092F\u0941\u0935\u093E (15\u201324), \u0935\u092F\u0938\u094D\u0915',
      location_label: '\u092E\u0941\u0916\u094D\u092F \u0938\u094D\u0925\u093E\u0928',
      location_placeholder: '\u0936\u0939\u0930 \u092F\u093E \u0907\u0932\u093E\u0915\u093E',
      outreach_label: '\u092A\u0939\u0941\u0902\u091A',
      outreach_placeholder: '\u0915\u093F\u0924\u0928\u0947 \u0932\u094B\u0917\u094B\u0902 \u0924\u0915 \u092A\u0939\u0941\u0902\u091A \u0938\u0915\u0924\u0947 \u0939\u0948\u0902?',
      email_label: '\u0908\u092E\u0947\u0932 *',
      email_placeholder: 'you@example.com',
      password_label: '\u092A\u093E\u0938\u0935\u0930\u094D\u0921 *',
      password_placeholder: '\u090F\u0915 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u092A\u093E\u0938\u0935\u0930\u094D\u0921 \u091A\u0941\u0928\u0947\u0902',
      submit: '\u0906\u0935\u0947\u0926\u0928 \u091C\u092E\u093E \u0915\u0930\u0947\u0902',
      success_title: '\u0906\u0935\u0947\u0926\u0928 \u091C\u092E\u093E \u0939\u094B \u0917\u092F\u093E',
      success_body: '\u0927\u0928\u094D\u092F\u0935\u093E\u0926\u0964 \u0939\u092E \u091C\u093E\u0902\u091A \u0915\u0930\u0915\u0947 \u091C\u0932\u094D\u0926 \u0939\u0940 \u0908\u092E\u0947\u0932 \u092A\u0930 \u091C\u0935\u093E\u092C \u0926\u0947\u0902\u0917\u0947\u0964',
      go_home: '\u0915\u092E\u094D\u092F\u0941\u0928\u093F\u091F\u0940 \u092A\u0930 \u091C\u093E\u090F\u0902',
    },
    tour: {
      back: '\u0935\u093E\u092A\u0938',
      next: '\u0905\u0917\u0932\u093E',
      lets_go: '\u091A\u0932\u0947\u0902',
      stars_earned_50: '+50 \u0938\u093F\u0924\u093E\u0930\u0947 \u0915\u092E\u093E\u090F!',
      welcome_subtitle: '\u0915\u092E\u094D\u092F\u0941\u0928\u093F\u091F\u0940 \u092E\u0947\u0902 \u0906\u092A\u0915\u093E \u0938\u094D\u0935\u093E\u0917\u0924 \u0939\u0948',
      welcome_title: 'socialstar \u092E\u0947\u0902 \u0906\u092A\u0915\u093E \u0938\u094D\u0935\u093E\u0917\u0924',
      welcome_body: '\u091C\u0939\u093E\u0901 \u0906\u092A\u0915\u0947 \u091A\u092F\u0928 \u0906\u092A\u0915\u0940 \u092F\u093E\u0924\u094D\u0930\u093E \u0924\u092F \u0915\u0930\u0924\u0947 \u0939\u0948\u0902 \u2014 \u0938\u0940\u0916\u0947\u0902 \u0914\u0930 \u0915\u092E\u093E\u090F\u0902',
      feature_exposure: '\u092A\u0939\u091A\u093E\u0928 \u092C\u0928\u093E\u090F\u0902 \u0914\u0930 \u0928\u0947\u091F\u0935\u0930\u094D\u0915 \u092C\u0922\u093C\u093E\u090F\u0902',
      feature_learning: '\u0928\u090F \u0915\u094C\u0936\u0932 \u0938\u0940\u0916\u0947\u0902 \u0914\u0930 \u0906\u0924\u094D\u092E\u0935\u093F\u0936\u094D\u0935\u093E\u0938 \u092C\u0922\u093C\u093E\u090F\u0902',
      feature_enjoy: '\u092A\u0938\u0902\u0926 \u0915\u0940 \u0917\u0924\u093F\u0935\u093F\u0927\u093F\u092F\u094B\u0902 \u092E\u0947\u0902 \u092D\u093E\u0917 \u0932\u0947\u0902',
      feature_rewards: '\u092D\u093E\u0917\u0940\u0926\u093E\u0930\u0940 \u092A\u0930 \u0907\u0928\u093E\u092E \u092A\u093E\u090F\u0902',
      age_confirm: '\u092E\u0948\u0902 \u092A\u0941\u0937\u094D\u091F\u093F \u0915\u0930\u0924\u093E/\u0924\u0940 \u0939\u0942\u0901 \u0915\u093F \u092E\u0947\u0930\u0940 \u0909\u092E\u094D\u0930 18 \u0935\u0930\u094D\u0937 \u092F\u093E \u0905\u0927\u093F\u0915 \u0939\u0948 \u0914\u0930 \u092E\u0948\u0902 socialstar \u0915\u0940 \u0936\u0930\u094D\u0924\u094B\u0902 \u0938\u0947 \u0938\u0939\u092E\u0924 \u0939\u0942\u0901\u0964',
      events_near_you_title: '\u0906\u092A\u0915\u0947 \u092A\u093E\u0938 \u0915\u0947 \u0907\u0935\u0947\u0902\u091F',
      events_near_you_body: '\u0906\u092A\u0915\u0947 \u0936\u0939\u0930 \u092E\u0947\u0902 \u0939\u094B\u0928\u0947 \u0935\u093E\u0932\u0947 \u0907\u0935\u0947\u0902\u091F \u2014 \u0915\u094D\u0932\u092C, \u0938\u0940\u0916\u0928\u0947 \u0915\u0947 \u0938\u0924\u094D\u0930 \u0914\u0930 \u0938\u0939\u092F\u094B\u0917',
      sample_chip_sports: '\u0916\u0947\u0932',
      sample_chip_stars: '30 \u0938\u093F\u0924\u093E\u0930\u0947',
      sample_action_title: '\u0917\u0932\u0940 \u0915\u094D\u0930\u093F\u0915\u0947\u091F \u092E\u0948\u091A \u092E\u0947\u0902 \u091C\u0941\u0921\u093C\u0947\u0902',
      sample_action_sub: '\u0938\u093E\u0925 \u0916\u0947\u0932\u0947\u0902 \u0914\u0930 \u0907\u0928\u093E\u092E \u092A\u093E\u090F\u0902',
      label_pincode: '\u0906\u092A\u0915\u093E \u092A\u093F\u0928\u0915\u094B\u0921',
      placeholder_pincode: '6 \u0905\u0902\u0915\u094B\u0902 \u0915\u093E \u092A\u093F\u0928\u0915\u094B\u0921 \u0926\u0930\u094D\u091C \u0915\u0930\u0947\u0902',
      pincode_privacy: '\u0906\u092A\u0915\u0947 \u0907\u0932\u093E\u0915\u0947 \u0915\u0940 \u0917\u0924\u093F\u0935\u093F\u0927\u093F\u092F\u093E\u0901 \u0926\u093F\u0916\u093E\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0939\u0940 \u0907\u0938\u094D\u0924\u0947\u092E\u093E\u0932 \u0939\u094B\u0917\u093E\u0964 \u0915\u093F\u0938\u0940 \u0924\u0940\u0938\u0930\u0947 \u092A\u0915\u094D\u0937 \u0915\u094B \u0936\u0947\u092F\u0930 \u0928\u0939\u0940\u0902 \u0915\u093F\u092F\u093E \u091C\u093E\u090F\u0917\u093E\u0964',
      join_events_title: '\u092A\u0938\u0902\u0926 \u0915\u0947 \u0907\u0935\u0947\u0902\u091F \u092E\u0947\u0902 \u091C\u0941\u0921\u093C\u0947\u0902',
      join_events_body: '\u0939\u092E\u0947\u0902 \u092C\u0924\u093E\u090F\u0902 \u0915\u094D\u092F\u093E \u092E\u0939\u0924\u094D\u0935\u092A\u0942\u0930\u094D\u0923 \u0939\u0948 \u2014 \u0939\u092E \u0906\u092A\u0915\u0947 \u092B\u093C\u0940\u0921 \u092E\u0947\u0902 \u0938\u0939\u0940 \u090F\u0915\u094D\u0936\u0928 \u0926\u093F\u0916\u093E\u090F\u0902\u0917\u0947\u0964',
      label_interests: '\u0930\u0941\u091A\u093F\u092F\u093E\u0901',
      label_income: '\u0935\u093E\u0930\u094D\u0937\u093F\u0915 \u0906\u092F',
      interest_fun: '\u092E\u0938\u094D\u0924\u0940 \u0914\u0930 \u0916\u0947\u0932',
      interest_learning: '\u0938\u0940\u0916 \u0914\u0930 \u0935\u093F\u0915\u093E\u0938',
      interest_creativity: '\u0930\u091A\u0928\u093E\u0924\u094D\u092E\u0915\u0924\u093E',
      interest_entertainment: '\u092E\u0928\u094B\u0930\u0902\u091C\u0928',
      income_low_line1: '\u20B9 2 \u0932\u093E\u0916 \u0938\u0947 \u0915\u092E',
      income_low_line2: '\u092A\u094D\u0930\u0924\u093F \u0935\u0930\u094D\u0937',
      income_high_line1: '\u20B9 2 \u0932\u093E\u0916 \u092F\u093E \u0905\u0927\u093F\u0915',
      income_high_line2: '\u092A\u094D\u0930\u0924\u093F \u0935\u0930\u094D\u0937',
      income_privacy: '\u0906\u092F \u091C\u093E\u0928\u0915\u093E\u0930\u0940 \u0939\u092E\u0947\u0902 \u0938\u0939\u0940 \u0905\u0935\u0938\u0930 \u091C\u094B\u0921\u093C\u0928\u0947 \u092E\u0947\u0902 \u092E\u0926\u0926 \u0915\u0930\u0924\u0940 \u0939\u0948\u0964 \u0917\u094B\u092A\u0928\u0940\u092F \u0930\u0916\u0940 \u091C\u093E\u090F\u0917\u0940\u0964',
      earn_stars_title: '\u0938\u093F\u0924\u093E\u0930\u0947 \u0915\u092E\u093E\u090F\u0902, \u0905\u0938\u0932\u0940 \u0907\u0928\u093E\u092E \u092A\u093E\u090F\u0902',
      earn_stars_body: '\u0939\u0930 \u090F\u0915\u094D\u0936\u0928 \u092A\u0930 \u0938\u093F\u0924\u093E\u0930\u0947 \u092E\u093F\u0932\u0924\u0947 \u0939\u0948\u0902\u0964 \u0905\u092A\u094D\u092A \u092E\u0947\u0902 \u0939\u0940 \u0935\u093E\u0909\u091A\u0930 \u0914\u0930 \u0930\u0940\u091A\u093E\u0930\u094D\u091C \u0915\u0947 \u0932\u093F\u090F \u0930\u0940\u0921\u0940\u092E \u0915\u0930\u0947\u0902\u0964',
      reward_vouchers: '\u0935\u093E\u0909\u091A\u0930',
      reward_vouchers_sub: '\u0915\u093F\u0930\u093E\u0928\u093E, \u0936\u0949\u092A\u093F\u0902\u0917 \u0914\u0930 \u0905\u0927\u093F\u0915',
      reward_mobile: '\u092E\u094B\u092C\u093E\u0907\u0932 \u0930\u0940\u091A\u093E\u0930\u094D\u091C',
      reward_mobile_sub: '\u0915\u094B\u0908 \u092D\u0940 \u0928\u0947\u091F\u0935\u0930\u094D\u0915',
      reward_more: '\u0914\u0930 \u092C\u0939\u0941\u0924 \u0915\u0941\u091B',
      reward_more_sub: '\u0928\u090F \u0930\u093F\u0935\u093E\u0930\u094D\u0921 \u091C\u0941\u0921\u093C\u0924\u0947 \u0930\u0939\u0924\u0947 \u0939\u0948\u0902',
      badge_redeem: '\u0930\u0940\u0921\u0940\u092E',
      badge_soon: '\u091C\u0932\u094D\u0926\u0940',
      all_set: '\u0906\u092A \u0924\u0948\u092F\u093E\u0930 \u0939\u0948\u0902 \u2014 \u090F\u0915\u094D\u0936\u0928 \u092A\u0942\u0930\u0947 \u0915\u0930\u0947\u0902 \u0914\u0930 \u0938\u093F\u0924\u093E\u0930\u0947 \u092C\u0922\u093C\u0924\u0947 \u0926\u0947\u0916\u0947\u0902\u0964',
    },
  },
};

export default communityI18n;
